<template>
  <div class="hcl-form-fields-selector">
    <hcl-dropdown
      v-if="chooserDropdownMenuItemsSelectableCount > 0"
      ref="chooserDropdownRef"
      icon="eii--fa6-solid--table-columns"
      :responsive=false
      button-class="btn-outline-default"
      :title="chooserDropdownText"
    >
      <a
        class="dropdown-item"
        @click="onChooserMenuItemAllClicked()"
      >
        <i
          v-if="chooserAllSelected"
          class="eii--fa6-solid--square-check"
        ></i>
        <i
          v-if="!chooserAllSelected"
          class="eii--fa6-regular--square"
        ></i>
        {{ chooserDropdownTextAll }}
      </a>
      <div class="dropdown-divider"></div>
      <template v-for="item in chooserDropdownMenuItemsSelectable">
        <a
          class="dropdown-item"
          v-if="item.selectable"
          :key="item.name"
          :id="'chooser-item-' + item.name"
          @click="onChooserMenuItemClicked(item)"
        >
          <i
            v-if="item.selected"
            class="eii--fa6-solid--square-check"
          ></i>
          <i
            v-if="!item.selected"
            class="eii--fa6-regular--square"
          ></i>
          {{ item.title }}
        </a>
      </template>
    </hcl-dropdown>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject } from 'vue'

import HclDropdown from '@/components/Dropdown/Dropdown.vue'

import { DefinitionFormConfig, DefinitionFormField, DefinitionFormFieldField } from '@/components/types'

interface Props {
  /**
   * Definition as an JS object.
   */
  config: DefinitionFormConfig
}

const props = withDefaults(defineProps<Props>(), {
  config: undefined
})

interface MenuItem {
  type: string
  name: string
  selectable: boolean
  selected: boolean
  title: string
}

const fields: DefinitionFormField[] = inject('definitionFields', [])

const chooserAllSelected = ref(false)

const chooserDropdownMenuItems = computed(() => {
  const items: MenuItem[] = []

  for (const field of fields) {
    let item: MenuItem | undefined

    if (field.field.group) {
      const group = items.filter((item) => {
        return item.type === 'group' && item.name === field.field.group?.name
      })

      if (group.length === 0) {
        item = {
          type: 'group',
          name: field.field.group?.name ?? '',
          selectable: field.chooser.selectable,
          selected: field.chooser.selected,
          title: field.field.group?.title ?? ''
        }
      }
    } else {
      item = {
        type: 'field',
        name: field.field.name,
        selectable: field.chooser.selectable,
        selected: field.chooser.selected,
        title: field.field.title
      }
    }

    if (typeof item !== 'undefined') items.push(item)
  }

  return items
})

const chooserDropdownMenuItemsSorted = computed(() => {
  return chooserDropdownMenuItems.value.sort(
    (a, b) => (a.title > b.title) ? 1 : ((b.title > a.title) ? -1 : 0)
  )
})

const chooserDropdownMenuItemsSelectable = computed(() => {
  const selectable = chooserDropdownMenuItemsSorted.value.filter((item) => {
    return item.selectable
  })

  return selectable
})

const chooserDropdownMenuItemsSelectableCount = computed(() => {
  return chooserDropdownMenuItemsSelectable.value.length
})

const chooserDropdownRef = ref()

const chooserDropdownText = computed(() => {
  return props.config.choosertext
    ? props.config.choosertext
    : 'Suchoptionen'
})

const chooserDropdownTextAll = computed(() => {
  return props.config.choosertext
    ? props.config.choosertextall
    : 'Alle'
})

const setFieldSelection = (item: MenuItem, state: boolean) => {
  if (item.type === 'group') {
    let first: DefinitionFormFieldField | undefined = undefined

    for (const field of fields) {
      if (field.field.group?.name === item.name) {
        if (first === undefined) first = field.field
        field.chooser.selected = state
      }
    }

    if (first === undefined) return

    first.focus = item.selected
  }

  if (item.type === 'field') {
    for (const field of fields) {
      if (field.field.group === undefined && field.field.name === item.name) {
        field.chooser.selected = state
        field.field.focus = true
      }
    }
  }
}

const toggleFieldSelection = (item: MenuItem) => {
  if (item.type === 'group') {
    let first: DefinitionFormFieldField | undefined = undefined

    for (const field of fields) {
      if (field.field.group?.name === item.name) {
        if (first === undefined) first = field.field
        field.chooser.selected = !field.chooser.selected
      }
    }

    if (first === undefined) return

    first.focus = item.selected
  }

  if (item.type === 'field') {
    for (const field of fields) {
      if (field.field.group === undefined && field.field.name === item.name) {
        field.chooser.selected = !field.chooser.selected
        field.field.focus = true
      }
    }
  }
}

const onChooserMenuItemAllClicked = () => {
  if (chooserDropdownRef.value?.closeMenu) chooserDropdownRef.value.closeMenu()

  chooserAllSelected.value = !chooserAllSelected.value

  if (chooserAllSelected.value) {
    for (let item of chooserDropdownMenuItemsSelectable.value) {
      if (item.selectable && !item.selected) {
        setFieldSelection(item, chooserAllSelected.value)
      }
    }
  } else {
    for (let item of chooserDropdownMenuItemsSelectable.value) {
      if (item.selectable) {
        setFieldSelection(item, chooserAllSelected.value)
      }
    }
  }
}

const onChooserMenuItemClicked = (item: MenuItem) => {
  if (chooserDropdownRef.value?.closeMenu) chooserDropdownRef.value.closeMenu()

  toggleFieldSelection(item)
}
</script>
